/**
 * Somebody asking for something, answered where it happened.
 *
 * A friend request or a trade request arrives while somebody is playing, and
 * what they want is to say yes or no without going to look for it. So it appears
 * where they are looking, with two buttons, and takes itself away again if it is
 * ignored: a request that waits forever is a request that has to be dismissed,
 * and dismissing things is work nobody asked for.
 *
 * Nothing here decides anything. Answering sends the ordinary act for that kind
 * of request, and what the lists then say comes back from the world.
 */

import { useEffect, useState } from 'react';
import { AskKind, type AskKindValue } from '@inverse/protocol';

/** One thing being asked, and when it arrived, so it can be taken away again. */
export interface Ask {
  id: number;
  kind: AskKindValue;
  name: string;
  at: number;
}

/**
 * How long one stands before it fades.
 *
 * Five seconds: long enough to read a name and reach for a button, short enough
 * that a request somebody has decided to ignore does not sit on their screen for
 * the rest of the afternoon. A friend request that runs out is still waiting in
 * the friends list, and a trade request that runs out was never worth keeping,
 * because the person who sent it has almost certainly walked off.
 */
const HELD_MS = 5000;

/** How long the fade itself takes, which the stylesheet matches. */
const FADE_MS = 600;

export function Requests({
  asks,
  onAnswer,
  onDone,
}: {
  asks: readonly Ask[];
  onAnswer: (ask: Ask, yes: boolean) => void;
  /** Takes away whatever has had its time, so the list does not grow forever. */
  onDone: (before: number) => void;
}): JSX.Element | null {
  const [now, setNow] = useState(() => Date.now());

  // A beat rather than a frame. Nothing here moves; this is only here to notice
  // when one has been up long enough.
  useEffect(() => {
    if (asks.length === 0) return;

    const beat = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(beat);
  }, [asks.length]);

  useEffect(() => {
    if (asks.length === 0) return;
    onDone(now - HELD_MS - FADE_MS);
  }, [now, asks.length, onDone]);

  if (asks.length === 0) return null;

  return (
    <div className="asks">
      {asks.map((ask) => {
        const age = now - ask.at;
        const going = age > HELD_MS;

        return (
          <div className={going ? 'ask going' : 'ask'} key={ask.id}>
            <span className="ask-said">
              <strong>{ask.name}</strong>
              {ask.kind === AskKind.Trade
                ? ' wants to trade.'
                : ask.kind === AskKind.Party
                  ? ' asks you into their party.'
                  : ' wants to be your friend.'}
            </span>

            <button type="button" className="ask-yes" onClick={() => onAnswer(ask, true)}>
              Accept
            </button>
            <button type="button" className="ask-no" onClick={() => onAnswer(ask, false)}>
              Decline
            </button>
          </div>
        );
      })}
    </div>
  );
}
